// How a single-value (KPI) result is written on the tile. Pure, so the rules can be
// pinned by tests and reused by the wizard preview and the card alike.
//
// The agent is told to keep units OUT of the value (shared/widget_schemas.py
// widget_instruction), so `unit` is a separate suffix here. Locale formatting is
// the browser's — thousands separators and the decimal mark follow the viewer —
// and precision is capped at two decimals. No compact notation ("1.2M"): the PRD
// asks for separators, and a rounded-off figure would disagree with the same
// question asked in chat.

import { formatDisplayNumber } from '../../../../utils/numberFormat';

const PLACEHOLDER = '—';

export function formatKpiNumber(value, locale) {
  return formatDisplayNumber(value, locale) ?? PLACEHOLDER;
}

// '%' hugs the number (42%); any other unit is a spaced suffix (1,204 hours).
export function formatKpiValue(value, unit, locale) {
  const number = formatKpiNumber(value, locale);
  if (number === PLACEHOLDER) return number;
  const u = (unit || '').trim();
  if (!u) return number;
  if (u === '%') return `${number}%`;
  return `${number} ${u}`;
}

// The one string the tile shows, for either value kind. Text answers are shown as
// given; a text kind with nothing in it is the placeholder, never "undefined".
export function kpiDisplayValue(payload, locale) {
  if (!payload || typeof payload !== 'object') return PLACEHOLDER;
  const kind = String(payload.value_kind || '').trim().toLowerCase();
  if (kind === 'text') {
    const t = String(payload.value_text ?? '').trim();
    return t || PLACEHOLDER;
  }
  return formatKpiValue(payload.value, payload.unit, locale);
}

// PRD-1578: a KPI card shows the value and ONE label, with no title above it. The
// label is the author's (`widget.display_label`) when they have edited it, otherwise
// the generated unit and subtitle run together ("shifts scheduled this week"). A WORD
// unit -- lowercase, three letters or more -- moves into the label, because it reads as
// part of it. Everything else stays on the number: symbols ('%', '$', '°C'), and the
// abbreviations and magnitudes that only make sense attached to it ('GB', 'kg', 'ms',
// 'USD', 'k', 'M'), where "12" over "M revenue" would misstate the value (review on #249).
const isWordUnit = (unit) => /^[a-z]{3,}(?:[\s-][a-z]+)*$/.test(unit);

export function generatedKpiLabel(payload) {
  if (!payload || typeof payload !== 'object') return '';
  const isText = String(payload.value_kind || '').trim().toLowerCase() === 'text';
  const unit = String(payload.unit ?? '').trim();
  const words = isText || !isWordUnit(unit) ? [] : [unit];
  words.push(String(payload.value_label ?? '').trim());
  return words.filter(Boolean).join(' ');
}

export function kpiCardLabel(payload, displayLabel) {
  return String(displayLabel ?? '').trim() || generatedKpiLabel(payload);
}

export function kpiCardValue(payload, locale) {
  if (!payload || typeof payload !== 'object') return PLACEHOLDER;
  if (String(payload.value_kind || '').trim().toLowerCase() === 'text') return kpiDisplayValue(payload, locale);
  const unit = String(payload.unit ?? '').trim();
  return formatKpiValue(payload.value, isWordUnit(unit) ? '' : unit, locale);
}

// Several rows tied for the one value a KPI shows: the card shows one, picked the same way
// on every run, and says how many others share it. Nothing when there is no tie.
export function kpiTiedLabel(payload) {
  const others = Number(payload?.tied_others);
  return Number.isInteger(others) && others > 0 ? `+${others} tied` : '';
}

export { PLACEHOLDER as KPI_PLACEHOLDER };
